import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { environment } from '@environment/environment';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Observable, of } from 'rxjs';

import { AuthHttp } from '@global-service//http/http.service';
import { customConsoleLog } from '@global-utiles/custom-console-log';
import { StatusApi, ProgresoFichero } from '.';
import { CODIGOS_ERROR_MITO, CODIGOS_ERROR_HTTP } from './codigos-error';

type callApiType = 'get' | 'post' | 'put' | 'patch' | 'delete';
type DefaultBoolean<T> = T extends boolean ? T : boolean;

interface ConfigApi<T> {
  getParams?: any;
  postData?: any;
  loading?: StatusApi; // Clase que actualizamos en la llamada a callApi para llevar el control de los estados del servicio. Loading, Done, Error.
  toastError?: string; // Utilizado para mostrar un mensage customizado en el toast de error. Null si no queremos mostrar el toast
  toastSuccess?: string;
  mockResult?: T;
  mockError?: string;
  oldApi?: boolean;
  environment?: string;
  keyResult?: string;
  ignoreHttpStatusCodes?: number[];
}

interface ConfigApiProgress<T> {
  progresoFichero: ProgresoFichero;
  environment?: string;
  toastError?: string; // Utilizado para mostrar un mensage customizado en el toast de error. Null si no queremos mostrar el toast
  toastSuccess?: string;
}
interface HttpMitoError {
  message: string;
  code: number;
  data: {
    error: any;
  };
}
@Injectable()
export class GeneralApiService {
  constructor(private readonly http: AuthHttp, private readonly toastr: ToastrService) {}

  private decodeUnicodeEscapes(value: string): string {
    try {
      return JSON.parse(`"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    } catch {
      return value;
    }
  }

  private prettifyValidationMessage(rawMessage: string): string {
    if (!rawMessage) return rawMessage;
    const decodedMessage = this.decodeUnicodeEscapes(rawMessage);
    const match = /Errores de validación:\s*(\{.*\})/s.exec(decodedMessage);
    if (!match) return decodedMessage;

    try {
      const payload = JSON.parse(match[1]);
      const lines = Object.entries(payload).map(([field, msg]) => {
        const cleanField = field.replace(/(?:^\[|\]$)/g, '').replace(/_/g, ' ');
        return `- ${cleanField}: ${String(msg)}`;
      });
      return `Errores de validación:\n${lines.join('\n')}`;
    } catch {
      return decodedMessage;
    }
  }

  private handleGeneralError(status: number) {
    this.toastr.error(CODIGOS_ERROR_HTTP[status] || 'Error no encontrado. Contacte con soporte ténico');
    return status;
  }
  private handleSiteError(siteError: any, toastError: string | null): number {
    let message = '';
    let error = null;
    const validationError = siteError?.excepcion || siteError?.error?.excepcion;

    if (siteError?.error) {
      message =
        siteError.error?.message ||
        siteError.error?.exception ||
        siteError.error?.msg ||
        (typeof siteError.error === 'string' ? siteError.error : '');
      error = siteError.error;
    } else {
      message =
        siteError?.message ||
        siteError?.exception ||
        siteError?.excepcion ||
        siteError?.mensaje?.msg ||
        siteError?.msg ||
        '';
      error = siteError;
    }

    if (validationError) {
      message = validationError.detail || validationError.title || '';
      if (!message && Array.isArray(validationError.violations)) {
        message = validationError.violations
          .map(({ propertyPath, title }) => `${propertyPath}: ${title}`)
          .join('\n');
      }
    }

    if (!message) {
      message = 'Error no encontrado. Contacte con soporte ténico';
    }
    message = this.prettifyValidationMessage(message);

    if (toastError === null) return error;
    const toastMessage = (toastError || message).replace(/\n/g, '<br>');
    this.toastr.warning(toastMessage, '', { enableHtml: true });
    return siteError.code;
  }
  private handleMitoError(mitoError: HttpMitoError) {
    this.toastr.warning(CODIGOS_ERROR_MITO[mitoError.code] || 'Código de error no encontrado. Contacte con el servicio ténico');
    return mitoError.code;
  }
  private returnMock<T>(mockData: T, error: string) {
    return of({ data: mockData }).pipe(
      delay(1000),
      map((res) => {
        if (error) throw new Error('Mock Error');
        return res;
      })
    );
  }
  callApi<T>(type: callApiType, url: string, config: ConfigApi<T> = {}) {
    let httpCall: Observable<{ data: T }>;
    const globalUrl = config.environment ? config.environment + url : environment.apiURLs.private + url;
    if (type === 'get') {
      httpCall = this.http.get<{ data: T }>(globalUrl, config.getParams);
    }
    if (type === 'post') {
      const data = config.oldApi ? config.postData : { data: config.postData };
      httpCall = this.http.post<{ data: T }>(globalUrl, data);
    }
    if (type === 'put') {
      const data = config.oldApi ? config.postData : { data: config.postData };
      httpCall = this.http.put<{ data: T }>(globalUrl, data);
    }
    if (type === 'patch') {
      const data = config.oldApi ? config.postData : { data: config.postData };
      httpCall = this.http.patch<{ data: T }>(globalUrl, data);
    }
    if (type === 'delete') {
      httpCall = this.http.delete<{ data: T }>(globalUrl, config.getParams);
    }
    const call = config.mockResult ? this.returnMock<T>(config.mockResult, config.mockError) : httpCall;
    if (config.loading) config.loading.setLoading();
    customConsoleLog('Call API', 'blue', 15, `URL: ${globalUrl} \n`, 'Data: ', config.getParams || config.postData);
    return lastValueFrom<T>(
      call.pipe(
        map((result) => {
          customConsoleLog('Result API', 'green', 15, `URL: ${globalUrl} \n`, 'Result: ', result);
          if (config.loading) config.loading.setDone();
          if (config.toastSuccess) this.toastr.success(config.toastSuccess);
          if (config.keyResult === null) return result;
          if (result === null) return null; // Post without body
          return result[config.keyResult || 'data'];
        }),
        catchError((httpError: HttpErrorResponse) => {
          customConsoleLog('Error API', 'red', 15, `URL: ${globalUrl} \n`, 'Error: ', httpError);
          if (config.ignoreHttpStatusCodes?.includes(httpError.status)) {
            if (config.loading) config.loading.setDone();
            return of(null as unknown as T);
          }
          let error: number = null;
          if (typeof httpError.error === 'string') {
            // Error tipo http
            error = this.handleGeneralError(httpError.status);
            throw error;
          }
          if (httpError.status !== 400 && httpError.status !== 422) {
            // La variable status no la tenemos como parametro en la respuesta de error ni en site ni en mito. Si se da el caso es que hemos el BE ha entrado por un escenario no conocido.
            error = this.handleGeneralError(httpError.status);
            throw error;
          }
          if (config.environment === environment.apiURLs.privatemito) error = this.handleMitoError(httpError.error);
          else error = this.handleSiteError(httpError.error, config.toastError);
          throw error;
        }),
        catchError((error: number) => {
          if (config.loading) config.loading.setError(error?.toString());
          throw error;
        })
      )
    );
  }
  callApiProgress<T, K = boolean>(datos: T, url: string, config: ConfigApiProgress<T>) {
    const globalUrl = config.environment ? config.environment + url : environment.apiURLs.private + url;
    config.progresoFichero.comenzarProgreso();
    customConsoleLog('Call API', 'blue', 15, `URL: ${globalUrl} \n`, 'Data: ', datos);
    return lastValueFrom(this.http.postProgress(globalUrl, datos).pipe(
      tap(resp => {
        if (resp.type === HttpEventType.UploadProgress) {
          config.progresoFichero.actualizarProgreso(resp.loaded, resp.total);
          customConsoleLog('Progress API', 'purple', 15, `URL: ${globalUrl} \n`, 'Progress: ', config.progresoFichero.progreso);
        }
        if (resp.type === HttpEventType.Response) {
          config.progresoFichero.finalizarProgreso();
          if (config.toastSuccess) this.toastr.success(config.toastSuccess);
          customConsoleLog('Result API', 'green', 15, `URL: ${globalUrl} \n`, 'Result: ', resp);
        }
      }),
      map(() => true as unknown as K),
      catchError(err => {
        config.progresoFichero.actualizarError();
        customConsoleLog('Error API', 'red', 15, `URL: ${globalUrl} \n`, 'Error: ', err);
        if (config.toastError) this.toastr.error(config.toastError);
        return of(err?.error?.data?.error as K);
      })
    ));
  }
}
